import type {
  Id,
  SummaryNode,
  SummaryItem,
  EditableGraph,
} from 'src/types/chatbot';
import type {EntityType} from 'src/types/ats-entities';

import * as React from 'react';
import useLabelTranslation from 'src/hooks/useLabelTranslation';

import {useEditorState} from './reducer';
import {validateNode} from './validation';
import {findAncestorQuestions, nodeToSummaryItem} from '../utils';

import EditorContainer from './container';
import {PadSection} from './common';
import Toggle from 'src/components/lib/toggle/toggle';
import {BorderlessTextInput} from 'src/components/lib/text-input/one-line';

import css from './summary.css';


export default function SummaryEditor({
  defaultNode,
  entityType,
  graph,
  hasSubmitted,
  edgeId,
  ...props
}: {
  entityType: EntityType;
  onClose: () => unknown;
  onSubmit: (arg1: SummaryNode) => unknown;
  defaultNode: SummaryNode;
  graph: EditableGraph;
  edgeId?: Id;
  actionName: 'add' | 'edit';
  hasSubmitted: boolean;
  readOnly: boolean;
}) {
  const {t} = useLabelTranslation();
  const {readOnly} = props;

  const [state, dispatch] = useEditorState({
    defaultNode,
  });

  const [summaryItems, setSummmaryItems] = React.useState(
    state.node.summary_items,
  );

  // @ts-expect-error - TS7006 - Parameter 'payload' implicitly has an 'any' type.
  const update = (payload) => dispatch({type: 'update', payload});
  const {node} = state;

  const parentNodeId = edgeId ? graph.edges.get(edgeId)?.from_node_id : null;
  const currentNode =
    edgeId != null && parentNodeId != null
      ? graph.nodes.get(parentNodeId)
      : defaultNode;

  const questionNodes = findAncestorQuestions(graph, currentNode);

  React.useEffect(() => {
    if (readOnly) {
      return;
    }

    const existingSummaryIds = summaryItems.map(({node_id}) => node_id);
    const newQuestions = questionNodes
      .filter((node) => !existingSummaryIds.includes(node.id))
      .map(nodeToSummaryItem);

    const updatedSummaryItems = [...summaryItems, ...newQuestions];

    const filteredSummaryItems = updatedSummaryItems.filter(({node_id}) =>
      questionNodes.map(({id}) => id).includes(node_id),
    );

    setSummmaryItems(filteredSummaryItems);
    update({
      summary_items: filteredSummaryItems,
    });
  }, []);

  React.useEffect(() => {}, []);

  const error = hasSubmitted ? validateNode(node, graph) : null;

  return (
    <EditorContainer
      className={css.popover}
      onUpdate={update}
      state={state}
      error={error}
      {...props}
    >
      <PadSection>{`${t(
        'Below_is_a_list_of_all_the_question_nodes_in_the_conversation_flow_This_list_will_update_automatically_if_a_question_node_is_added_or_deleted_To_exclude_a_question_in_the_summary_toggle_it_off',
        'Below is a list of all the question nodes in the conversation flow. This list will update automatically if a question node is added or deleted. To exclude a question in the summary, toggle it off',
      )}.`}</PadSection>
      <div className={css.table}>
        <div className={css.header}>
          <span>{t('Node_Name', 'Node Name')}</span>
          <span>{t('Short_Name', 'Short Name')}</span>
          <span className={css.toggle}>{`${t('Enabled', 'Enabled')}?`}</span>
        </div>
        <div className={css.body}>
          {[
            ...summaryItems.map((summaryItem, index) => {
              const updateItem = (
                change:
                  | {
                      enabled: boolean;
                    }
                  | {
                      short_name: string;
                    },
              ) => {
                const newItem = {...summaryItem, ...change} as const;
                const summary_items = node.summary_items.slice();
                summary_items[index] = newItem;
                update({summary_items});
                setSummmaryItems(summary_items);
              };
              const questionNode = questionNodes.find(
                (node) => node.id === summaryItem.node_id,
              );
              if (!questionNode) {
                return;
              }
              return (
                <div className={css.row} key={questionNode.id}>
                  <span>{questionNode.name}</span>
                  {/* @ts-expect-error - TS2786 - 'BorderlessTextInput' cannot be used as a JSX component. */}
                  <BorderlessTextInput
                    // @ts-expect-error - TS2322 - Type 'string' is not assignable to type 'never'.
                    value={summaryItem.short_name}
                    // @ts-expect-error - TS2322 - Type '(event: ChangeEvent<HTMLInputElement>) => void' is not assignable to type 'never'.
                    onChange={(event) =>
                      updateItem({short_name: event.currentTarget.value})
                    }
                    // @ts-expect-error - TS2322 - Type 'boolean' is not assignable to type 'never'.
                    disabled={readOnly}
                  />
                  <div className={css.toggle}>
                    <Toggle
                      checked={summaryItem.enabled}
                      onChange={(event) =>
                        updateItem({enabled: event.currentTarget.checked})
                      }
                      disabled={readOnly}
                    />
                  </div>
                </div>
              );
            }),
          ]}
        </div>
      </div>
    </EditorContainer>
  );
}
